import { useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import { ArrowUpRight, Check, Circle, LockKeyhole, Minus, Search, UserCheck } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { PHASES as SETUP_PHASES, STEP_COPY } from "@/components/schools/setup/steps";
import AccountBadge from "@/components/admin/AccountBadge";
import { Button } from "@/components/ui/button";
import { formatMoney, formatNumber, formatDate, formatDateTime, pluralize } from "@/lib/format";

const PHASES = {
  early_donations: { label: "Donations open", color: "bg-accent" },
  active: { label: "Reading active", color: "bg-success" },
  grace: { label: "Wrapping up", color: "bg-warning" },
};

export default function Dashboard({ stats, schools, awaiting_payment: awaiting, setup_schools: setup, live_campaigns: campaigns }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const filteredSchools = schools.filter(school =>
    (filter === "all" || school.account_status === filter) &&
    [school.name, school.contact?.name, school.contact?.email].filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout eyebrow="Overview" title="School activity" actions={<Link href="/admin/users" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">{pluralize(stats.total_users, "user")} <ArrowUpRight className="h-3.5 w-3.5" /></Link>}>
      <Head title="Admin · School activity" />
      <div className="space-y-5 -mt-4">
        <Panel title="Campaigns underway" count={campaigns.length} note={`${pluralize(stats.live_schools, "school")} · includes pre-reading donations`}>
          {campaigns.length === 0 ? <Empty>No campaigns underway yet.</Empty> : (
            <Table headings={["School / campaign", "Phase", "Raised", "Reading", "Latest activity", ""]}>
              {campaigns.map(campaign => {
                const phase = PHASES[campaign.display_state];
                return (
                  <tr key={campaign.id} className="hover:bg-secondary/20 transition-colors">
                    <Cell className="min-w-48">
                      <SchoolName school={campaign.school} />
                      <p className="text-xs text-muted-foreground mt-1">{campaign.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{pluralize(campaign.student_count, "student")} · {campaign.currency}</p>
                    </Cell>
                    <Cell className="min-w-36">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap"><span className={`h-1.5 w-1.5 rounded-full ${phase.color}`} />{phase.label}</span>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(campaign.start_on, { year: false })} – {formatDate(campaign.end_on, { year: false })}</p>
                    </Cell>
                    <Cell className="whitespace-nowrap tabular-nums">
                      <p className="font-semibold">{formatMoney(campaign.raised_cents)}</p>
                      <p className="text-xs text-accent mt-1">{formatMoney(campaign.recent_raised_cents)} in 7d</p>
                      <p className="text-xs text-muted-foreground mt-1">{pluralize(campaign.recent_donations, "gift")} in 7d</p>
                    </Cell>
                    <Cell className="whitespace-nowrap tabular-nums">
                      <p className="font-semibold">{formatNumber(campaign.minutes)} <span className="font-normal text-xs text-muted-foreground">min</span></p>
                      <p className="text-xs text-accent mt-1">{formatNumber(campaign.recent_minutes)} min in 7d</p>
                    </Cell>
                    <Cell className="min-w-36"><Activity label="Gift" at={campaign.last_donation_at} /><Activity label="Reading" at={campaign.last_reading_at} /></Cell>
                    <Cell><Impersonate school={campaign.school} /></Cell>
                  </tr>
                );
              })}
            </Table>
          )}
        </Panel>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-start">
          <Panel title="Setting up" count={stats.setting_up_schools} note="Setup wizard progress">
            {setup.length === 0 ? <Empty>No schools setting up.</Empty> : (
              <ul className="divide-y divide-border">
                {setup.map(entry => (
                  <li key={`${entry.school_id}-${entry.campaign?.id || "new"}`} className="px-4 py-3 hover:bg-secondary/20">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2"><SchoolName school={entry.school} /><AccountBadge status={entry.school.account_status} /></div>
                        <p className="text-xs text-muted-foreground mt-1">{entry.campaign?.name || "No campaign yet"} · {entry.start_on ? formatDate(entry.start_on) : "Dates not set"} · {pluralize(entry.student_count, "student")}</p>
                      </div>
                      <Impersonate school={entry.school} />
                    </div>
                    <WizardProgress entry={entry} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Awaiting payment" count={awaiting.length}>
            {awaiting.length === 0 ? <Empty>All caught up.</Empty> : (
              <ul className="divide-y divide-border">
                {awaiting.map(campaign => (
                  <li key={campaign.id}>
                    <Link href={campaign.school_path} className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-secondary/30">
                      <div className="min-w-0"><p className="font-medium text-sm">{campaign.school_name}</p><p className="text-xs text-muted-foreground mt-1">{campaign.name}</p><p className="text-xs text-muted-foreground mt-1">{campaign.plan_name} · {pluralize(campaign.student_count, "student")}</p></div>
                      <span className="font-semibold text-sm tabular-nums whitespace-nowrap">{formatMoney(campaign.price_cents)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <Panel title="Schools" count={schools.length} note="Newest signups first">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
            <div className="flex flex-wrap gap-1" aria-label="Filter schools by account status">
              {[["all", "All"], ["trial", "Trial"], ["paid", "Paid"], ["free", "Free"]].map(([key, label]) => (
                <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)} className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors ${filter === key ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-secondary"}`}>
                  {label}<span className="tabular-nums opacity-75">{key === "all" ? schools.length : schools.filter(s => s.account_status === key).length}</span>
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-muted-foreground"><Search className="h-3.5 w-3.5" /><span className="sr-only">Search schools or contacts</span><input type="search" placeholder="Find school or contact…" value={search} onChange={e => setSearch(e.target.value)} className="w-44 bg-transparent text-xs py-1.5 outline-none focus:ring-2 focus:ring-ring rounded-sm" /></label>
          </div>
          {filteredSchools.length === 0 ? <Empty>No schools match.</Empty> : (
            <Table headings={["School", "Account", "Contact", "Signed up", ""]}>
              {filteredSchools.map(school => (
                <tr key={school.id} className="hover:bg-secondary/20 transition-colors">
                  <Cell className="min-w-44"><SchoolName school={school} /><p className="mt-1 text-xs text-muted-foreground">{[school.city, school.province, school.archived && "Archived"].filter(Boolean).join(" · ")}</p></Cell>
                  <Cell><AccountBadge status={school.account_status} /></Cell>
                  <Cell><p className="text-xs">{school.contact?.name || "No contact"}</p><p className="text-xs text-muted-foreground mt-1">{school.contact?.email}</p></Cell>
                  <Cell className="text-xs text-muted-foreground whitespace-nowrap">{school.signed_up_ago}</Cell>
                  <Cell><Impersonate school={school} /></Cell>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
    </AdminLayout>
  );
}

function Panel({ title, count, note, children }) {
  return <section className="min-w-0 rounded-xl border border-border bg-card overflow-hidden"><header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-3"><h2 className="font-display text-lg font-semibold">{title}</h2><span className="rounded-md bg-secondary px-1.5 py-0.5 text-xs font-semibold tabular-nums">{count}</span>{note && <p className="text-xs text-muted-foreground ml-auto">{note}</p>}</header>{children}</section>;
}
function Table({ headings, children }) {
  return <div className="relative overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-secondary/30"><tr>{headings.map((heading, i) => <th key={i} scope="col" className="px-4 py-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">{heading || <span className="sr-only">Actions</span>}</th>)}</tr></thead><tbody className="divide-y divide-border">{children}</tbody></table></div>;
}
function Cell({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function SchoolName({ school }) {
  return <Link href={school.path} className="font-medium text-sm hover:text-primary hover:underline">{school.name}</Link>;
}
function Empty({ children }) {
  return <p className="px-4 py-5 text-xs text-muted-foreground">{children}</p>;
}
function Activity({ label, at }) {
  return <p className="text-xs text-muted-foreground mb-1" title={at ? formatDateTime(at) : undefined}><span className="text-foreground">{label}</span> · {at ? formatDate(at, { year: false }) : "None yet"}</p>;
}
function Impersonate({ school }) {
  const [busy, setBusy] = useState(false);
  const target = school.impersonation;
  if (!target) return <span className="text-xs text-muted-foreground whitespace-nowrap">No school admin</span>;
  return <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={busy} title={`Sign in as ${target.name} (${target.email})`} aria-label={`Impersonate ${target.name} at ${school.name}`} onClick={() => { setBusy(true); router.post(target.path, {}, { onFinish: () => setBusy(false) }); }}><UserCheck className="h-3.5 w-3.5" />{busy ? "Opening…" : "Impersonate"}</Button>;
}

function WizardProgress({ entry }) {
  const { progress, steps } = entry;
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between gap-3 text-xs">
        <p className="text-muted-foreground">Next: <span className="text-foreground font-medium">{STEP_COPY[entry.next_step].short}</span></p>
        <span className="tabular-nums text-muted-foreground whitespace-nowrap">{progress.complete}/{progress.total} complete</span>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2" aria-label="Setup phases">
        {SETUP_PHASES.filter(phase => phase.key !== "launch").map(phase => {
          const tasks = steps.filter(step => step.phase === phase.key);
          const complete = tasks.filter(step => ["done", "skipped"].includes(step.status)).length;
          return <div key={phase.key} title={`${phase.title}: ${complete}/${tasks.length} complete`}><div className="h-1 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-accent rounded-full" style={{ width: `${complete / tasks.length * 100}%` }} /></div><p className="mt-1 text-[10px] text-muted-foreground">{phase.title} <span className="tabular-nums">{complete}/{tasks.length}</span></p></div>;
        })}
      </div>
      <details className="mt-2 text-xs group">
        <summary className="cursor-pointer text-muted-foreground hover:text-primary focus-visible:outline-ring">{entry.ready_to_launch ? "Ready to launch" : `${pluralize(progress.required_remaining, "required step")} left`} · View steps</summary>
        <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mt-3 pb-1">
          {steps.map(step => {
            const Icon = { done: Check, skipped: Minus, blocked: LockKeyhole }[step.status] || Circle;
            return <li key={step.key} className="flex items-start gap-1.5"><Icon className={`h-3 w-3 shrink-0 mt-0.5 ${step.status === "done" ? "text-accent" : "text-muted-foreground"}`} /><div><span>{STEP_COPY[step.key].short}</span><span className="text-muted-foreground"> · {step.status.replaceAll("_", " ")}{step.tier === "required" ? " · required" : ""}</span>{step.overdue && <span className="text-warning-foreground"> · overdue</span>}{step.blocked_by.length > 0 && <p className="text-[10px] text-muted-foreground">Needs {step.blocked_by.map(key => STEP_COPY[key].short).join(", ")}</p>}</div></li>;
          })}
        </ol>
      </details>
    </div>
  );
}
